// Every word the interface shows, in each supported language. A screen reads
// its copy from here by key, so translating psy is editing this file and
// nothing else.
//
// `errors` translates the sentences the services answer with. The services
// write them in English for the log and the API; the screen looks the sentence
// up here and shows it as it came when there is no translation.
export const messages = {
  en: {
    today: 'Today',
    diary: 'Diary',
    tests: 'Tests',
    mainNavigation: 'Main',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    signOut: 'Sign out',
    signOutWithoutNote: 'Sign out without it',
    signOutAgain: 'Sign out again',
    signingOutNotes: 'Signing out. Notes are read-only until every note is saved.',
    noteReadOnly: 'Read-only while signing out',

    signInTitle: 'Continue with email',
    signInIntro: 'We’ll send a 6-digit code to your inbox. New and returning people use the same step.',
    email: 'Email',
    sendCode: 'Send code',

    codeTitle: 'Check your email',
    codeIntro: 'Enter the 6-digit code we sent to',
    code: 'Code',
    continue: 'Continue',
    resendIn: 'Resend code in',
    resend: 'Send a new code',
    codeResent: 'A new code is on its way.',
    differentEmail: 'Use a different email',

    yourProfile: 'Your profile',
    stepOf: 'Step {step} of 4',
    back: 'Back',
    previousStep: 'Previous step',
    finish: 'Finish',
    skipForNow: 'Skip for now',
    nameTitle: 'What’s your name?',
    nameIntro: 'As you’d like your clinician to see it.',
    firstName: 'First name',
    lastName: 'Last name',
    birthTitle: 'When were you born?',
    birthDate: 'Birth date',
    birthHint: 'Shown in your device’s date format. A future date isn’t accepted.',
    genderTitle: 'How do you describe your gender?',
    genderIntro: 'Not used for official records.',
    genders: {
      WOMAN: 'Woman',
      MAN: 'Man',
      NON_BINARY: 'Non-binary',
      SELF_DESCRIBED: 'I’d describe it differently',
      PREFER_NOT_TO_SAY: 'Prefer not to say'
    },
    fiscalTitle: 'Your Italian fiscal code',
    fiscalIntro: 'You can skip this and add it later in your profile.',
    fiscalCode: 'Fiscal code',
    fiscalPlaceholder: '16 characters, or 11 digits if temporary',
    diaryTitle: 'Diary',
    todayLower: 'today',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    dailyRecord: 'Daily record',
    record: 'Record',
    editRecord: 'Edit this record',
    nothingRecorded: 'Nothing recorded yet',
    nothingRecordedIntro: 'Mood, anxiety, irritability, sleep, weight and medication. It takes a minute.',
    notYet: 'This day has not happened yet.',
    completeness: { COMPLETE: 'Complete', PARTIAL: 'Partial', NONE: 'No entry' },
    mood: 'Mood',
    anxiety: 'Anxiety',
    irritability: 'Irritability',
    hoursSlept: 'Hours slept',
    weight: 'Weight',
    medication: 'Medication',
    skipped: 'Skipped',
    hours: 'hours',
    kg: 'kg',
    moods: [
      { name: 'Severely depressed', anchor: 'Significant impairment · not able to work' },
      { name: 'Moderately depressed', anchor: 'Significant impairment · able to work' },
      { name: 'Mildly depressed', anchor: 'Without significant impairment' },
      { name: 'Normal', anchor: '' },
      { name: 'Mildly elevated', anchor: 'Without significant impairment' },
      { name: 'Moderately elevated', anchor: 'Significant impairment · able to work' },
      { name: 'Severely elevated', anchor: 'Significant impairment · not able to work' }
    ],
    levels: [ 'None', 'Mild', 'Moderate', 'Severe' ],
    monthChart: 'Mood chart',
    legend: 'Legend',
    saveAsPdf: 'Save as PDF',
    preparingPdf: 'Preparing the PDF…',
    anxietyScale: 'Anxiety 0–3',
    irritabilityScale: 'Irritability 0–3',
    emptyColumn: 'Empty column · no record',
    emptyDays: 'Empty columns are days with no record. Weight is asked on the 28th.',
    close: 'Close',
    recordingFor: 'Recording for',
    recordStep: 'Daily record · {step} of 6',
    recordSteps: 'Steps of the daily record',
    next: 'Next',
    skip: 'Skip',
    saveRecord: 'Save record',
    moodQuestion: 'How was your mood today?',
    anxietyQuestion: 'How anxious did you feel today?',
    irritabilityQuestion: 'How irritable did you feel today?',
    sleepQuestion: 'How many hours did you sleep?',
    sleepHint: 'The period you agreed with your clinician.',
    weightHint: 'Your clinician asks for it on the 28th.',
    medicationQuestion: 'Medication',
    medicationHint: 'Write what you agreed with your clinician to note each day.',
    addMedication: 'Add medication',
    medicationName: 'Name',
    dose: 'Dose (mg)',
    add: 'Add',
    removeMedication: 'Remove {name}',
    removedNotRefreshed: 'Removed from your list. The list could not be updated yet; it will be on the next step.',
    noMedications: 'No medications on your list.',
    checkTitle: 'Check and save',
    checkHint: 'Empty answers stay empty.',
    edit: 'Edit',

    assignedToYou: 'Assigned to you',
    nothingAssigned: 'Nothing to fill in right now.',
    completedTests: 'Completed',
    notStarted: 'Not started',
    inProgress: 'In progress · draft',
    submitted: 'Submitted',
    submittedOn: 'Submitted {date}',
    fromClinician: 'From {name}',
    dueOn: 'Due {date}',
    answeredOf: '{answered} of {total} answered',
    repeatsEvery: 'Repeats every {days} days',
    start: 'Start',
    saveAndExit: 'Save and exit',
    draftSaved: 'Draft saved',
    questionOf: 'Question {position} of {total}',
    answeredCount: '{answered} answered',
    answered: 'Questions answered',
    previousQuestion: 'Previous question',
    draftNote: 'Your answers stay a draft, visible only to you, until you submit.',
    checkAnswers: 'Check your answers',
    backToQuestions: 'Back to questions',
    backToTests: 'Back to Tests',
    missingOne: '1 question has no answer yet. Answer every question to submit.',
    missingMany: '{count} questions have no answer yet. Answer every question to submit.',
    notAnswered: 'Not answered',
    yourClinician: 'your clinician',
    submitNote: 'When you submit, {name} can see your answers. Until then they are a draft only you can see.',
    submit: 'Submit',
    submitTo: 'Submit to {name}',
    yourAnswers: 'Your answers',
    submittedKept: 'Submitted {date}. Your answers are kept exactly as you sent them.',

    goodMorning: 'Good morning, {name}',
    goodAfternoon: 'Good afternoon, {name}',
    goodEvening: 'Good evening, {name}',
    dailyRecords: 'Daily records',
    thisWeek: 'This week',
    weekCounts: '{complete} complete · {partial} partial',
    toCome: 'still to come',
    nextAppointment: 'Next appointment',
    minutes: '{minutes} min',
    formats: { IN_PERSON: 'In person', VIDEO: 'Video', PHONE: 'Phone' },
    questionnairesBefore: 'Questionnaires before this session',
    submittedOf: '{submitted} of {total} submitted',
    newNoteFrom: 'New note from {name}',
    fromSession: 'From your session on {date}',
    todaysRecord: 'Today’s record',
    notRecordedYet: 'Not recorded yet · mood, anxiety, irritability, sleep, medication',
    partlyRecorded: 'Partly recorded · finish it when you can',
    allRecorded: 'All recorded',
    recordToday: 'Record today',
    editTodaysRecord: 'Edit today’s record',
    questionnaires: 'Questionnaires',
    draftDetail: 'Draft saved · {answered} of {total} answered',
    dueLower: 'due {date}',

    dashboard: 'Dashboard',
    patients: 'Patients',
    settings: 'Settings',
    invitation: 'Invitation',
    profileAndSettings: 'Profile & settings',
    profile: 'Profile',
    gender: 'Gender',
    notAdded: 'Not added',
    appearance: 'Appearance',
    appearances: {
      DEVICE: 'Device',
      LIGHT: 'Light',
      DARK: 'Dark'
    },
    appearanceDeviceHint: 'Follows your phone or computer: light by day, dark when the device is in dark mode.',
    appearanceChosenHint: 'Used on every device you sign in on.',

    invitationsWaiting: 'Invitations waiting',
    noInvitationsWaiting: 'No invitations are waiting for an answer.',
    invitedNotAnswered: 'Invited {date} · not answered yet',
    invitedExpired: 'Invited {date} · expired, send it again to reopen it',
    declinedOn: 'Declined {date}',
    invitePatient: 'Invite patient',
    searchPatients: 'Search patients',
    searchByName: 'Search by name',
    filter: 'Filter',
    active: 'Active',
    invited: 'Invited',
    noPatientsFound: 'Nobody here yet.',
    connectedSince: 'Connected since {date}',
    invitePatientTitle: 'Invite a patient',
    patientEmail: 'Patient’s email',
    nameForList: 'Name for your list · optional',
    nameForListHint: 'Shown only to you until the patient completes their profile.',
    whatHappensNext: 'What happens next',
    inviteStep1: 'They get an email with a link to the invitation.',
    inviteStep2: 'They sign in, or create an account, and accept or decline.',
    inviteStep3: 'Once they accept, they appear in your patient list and you can read their whole diary, including what they wrote before. Their journal stays private.',
    sendInvitation: 'Send invitation',
    invitationSentTo: 'Invitation sent to {email}.',
    pending: 'Pending',
    resendInvitation: 'Resend',
    withdraw: 'Withdraw',
    sentAgain: 'Sent again.',

    yourClinicians: 'Your clinicians',
    noClinicians: 'You are not connected with a clinician yet.',
    wantsToConnect: '{name} wants to connect',
    invitationFrom: 'Invitation from {date} · accept or decline',
    sentOn: 'Sent {date}',
    reviewInvitation: 'Review invitation',
    wantsToConnectWithYou: '{name} wants to connect with you',
    ifYouAccept: 'If you accept, {name} can',
    canPlanSessions: 'See and plan your sessions together',
    canAssignQuestionnaires: 'Assign you questionnaires and read what you submit',
    canWriteNotes: 'Write a note for you after a session',
    canReadDiary: 'Read your whole diary, including entries you have already written',
    journalStaysPrivate: 'Your journal stays private',
    acceptingSharesDiary: 'If you accept, {name} can read your whole diary, including entries you have already written. Your journal is never shared.',
    accept: 'Accept',
    decline: 'Decline',
    sentToOn: 'Sent to {email} on {date}. You can also answer later from Settings.',
    connectedWith: 'You’re connected with {name}',
    diaryNowShared: 'Your diary is now shared with {name}, including entries you had already written. Your journal stays private.',
    invitationDeclined: 'Invitation declined',
    declinedExplained: 'You’re not connected with {name}. Nothing of yours was shared. If this was a mistake, ask {name} to send a new invitation.',
    goToToday: 'Go to Today',
    invitationClosed: 'This invitation is no longer open',
    invitationClosedExplained: 'It was already answered, withdrawn, expired or sent to another email address. Your clinicians are listed in Settings.',
    openSettings: 'Open Settings',

    assignTest: 'Assign test',
    assignATest: 'Assign a test',
    patientRecord: 'Patient record',
    openTests: 'Open tests: {count}',
    searchTests: 'Search tests',
    assignedTests: 'Assigned tests',
    notSubmitted: 'Not submitted',
    noTestsFound: 'No tests assigned yet.',
    draftsStayWith: 'Drafts stay with {name}: a test opens here once it is submitted.',
    assignedOn: 'Assigned {date}',
    byClinician: 'by {name}',
    questionnaire: 'Questionnaire',
    searchByNameOrTopic: 'Search by name or topic',
    availableIn: 'Available in {languages}',
    noQuestionnairesFound: 'No questionnaire matches.',
    change: 'Change',
    languageNames: { en: 'English', it: 'Italian' },
    shownInLanguage: 'Shown in {name}’s language, {language}. A questionnaire with no version in it is shown in English.',
    shownInEnglish: '{name}’s language is {language}. This questionnaire has no {language} version, so it is shown in English.',
    shownOnlyIn: 'This questionnaire exists only in {language}, so it is shown in {language}.',
    instructionsFor: 'Instructions for {name} · optional',
    assignStep1: '{name} finds it in Tests and on Today.',
    assignStep2: '{name}’s answers stay private while the test is a draft.',
    assignStep3: 'Once {name} submits it, it is listed in their Tests tab.',
    assignTo: 'Assign to {name}',
    waitingFor: 'Waiting for {name}',
    chooseQuestionnaire: 'Choose a questionnaire to assign',

    toReview: 'To review',
    reviewed: 'Reviewed',
    reviewedLower: 'reviewed {date}',
    newToReview: '{count} new to review',
    questionnairesToReview: 'Questionnaires to review',
    nothingToReview: 'Nothing waits for your review.',
    submittedLower: 'submitted {date}',
    allTests: 'All tests',
    submittedAt: 'submitted {date}',
    notReviewed: 'Not reviewed',
    reviewedOn: 'Reviewed {date}',
    totalScore: 'Total',
    scores: 'Scores',
    answers: 'Answers',
    answersHint: 'Open an answer to see every option and write a note. Notes save as you type; only you see them, and they never reach {name} or a patient report.',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    note: 'Note',
    theirAnswer: '({name}’s answer)',
    yourNote: 'Your note',
    noteOnAnswer: 'Add a note on this answer',
    saving: 'Saving…',
    saved: 'Saved',
    yourReview: 'Your review',
    noteOnTest: 'Your note on this test',
    noteOnTestPlaceholder: 'Add a note on the whole test',
    reviewHint: 'Only you see it. Writing notes doesn’t mark the test as reviewed, and {name} never sees the review state.',
    markReviewed: 'Mark as reviewed',
    markNotReviewed: 'Mark as not reviewed',
    earlierAttempts: 'Earlier attempts',
    keptAsSubmitted: 'Each attempt is kept exactly as submitted.',

    loadFailed: 'Couldn’t load this page.',
    loadFailedIntro: 'Everything you saved is safe. Check your connection and try again.',
    progressFailed: 'Couldn’t load your latest progress.',
    tryAgain: 'Try again',

    landing: {
      signIn: 'Sign in',
      kicker: 'For people in therapy, and the clinicians who care for them',
      title: 'Keep track between sessions, together with your clinician.',
      intro: 'psy supports the care you already have: the daily record your clinician asks for, the questionnaires they assign, your sessions and the notes they write for you. On your phone or your computer, in English or Italian.',
      start: 'Sign in with email',
      invited: 'Invited by your clinician? Use the email address the invitation was sent to.',
      thisWeek: 'This week',
      week: [ 'M', 'T', 'W', 'T', 'F', 'S', 'S' ],
      nextAppointment: 'Next appointment',
      appointmentWhen: 'Thu 15 Oct · 17:00',
      appointmentHow: '50 min · In person',
      newNote: 'A new note from your clinician',
      forYou: 'For you',
      patient: [
        { title: 'Daily record', text: 'Mood, anxiety, irritability, sleep, weight and medication, one question at a time. Skip what you don’t know; nothing is filled in for you.' },
        { title: 'Questionnaires', text: 'Fill in what your clinician assigns, at your own pace. A draft stays yours until you submit it.' },
        { title: 'Sessions and notes', text: 'See your next appointment and read the note your clinician writes for you after a session.' },
        { title: 'Journal', text: 'Write what you want to remember or bring to your next session. It stays private.' }
      ],
      forClinicians: 'For clinicians',
      clinician: [
        { title: 'Your patients', text: 'Invite a patient by email and see everyone you care for in one list.' },
        { title: 'Questionnaires', text: 'Assign a questionnaire in the patient’s language, then review every answer and the published scores, with private notes.' },
        { title: 'Mood chart', text: 'Read a patient’s month as on your paper sheet: the seven mood states, anxiety, irritability, sleep and weight.' },
        { title: 'PDF reports', text: 'Save a submitted test or a month of the diary as a PDF, for your records.' }
      ],
      sharedTitle: 'What is shared, and with whom',
      shared: [
        'You connect only with a clinician who invites you, and only if you accept.',
        'Accepting lets that clinician read your diary, including entries you had already written. You see the clinicians you are connected with in Settings.',
        'Your journal is never shared.',
        'A clinician’s private notes stay with them. You see the note they write for you.',
        'psy makes no diagnosis and gives no medical advice.'
      ],
      howItStarts: 'How it starts',
      steps: [
        { title: 'Your clinician invites you', text: 'You get an email with a link to the invitation.' },
        { title: 'You sign in and accept', text: 'Sign in with a code sent to that email address. Before accepting, you read exactly what will be shared.' },
        { title: 'You keep track together', text: 'Record your days and fill in questionnaires; your clinician reads them ahead of your sessions.' }
      ],
      languages: 'In English or Italian, following your device’s language.'
    },

    errors: {}
  },
  it: {
    today: 'Oggi',
    diary: 'Diario',
    tests: 'Test',
    mainNavigation: 'Principale',
    openMenu: 'Apri il menu',
    closeMenu: 'Chiudi il menu',
    signOut: 'Esci',
    signOutWithoutNote: 'Esci senza salvarla',
    signOutAgain: 'Esci di nuovo',
    signingOutNotes: 'Uscita in corso. Le note sono in sola lettura finché non sono tutte salvate.',
    noteReadOnly: 'In sola lettura durante l’uscita',

    signInTitle: 'Continua con l’email',
    signInIntro: 'Ti mandiamo un codice di 6 cifre. Lo stesso passaggio vale per chi è nuovo e per chi torna.',
    email: 'Email',
    sendCode: 'Invia il codice',

    codeTitle: 'Controlla la tua email',
    codeIntro: 'Inserisci il codice di 6 cifre che abbiamo mandato a',
    code: 'Codice',
    continue: 'Continua',
    resendIn: 'Nuovo codice tra',
    resend: 'Invia un nuovo codice',
    codeResent: 'Un nuovo codice è in arrivo.',
    differentEmail: 'Usa un’altra email',

    yourProfile: 'Il tuo profilo',
    stepOf: 'Passo {step} di 4',
    back: 'Indietro',
    previousStep: 'Passo precedente',
    finish: 'Fine',
    skipForNow: 'Salta per ora',
    nameTitle: 'Come ti chiami?',
    nameIntro: 'Come vuoi che ti veda il tuo clinico.',
    firstName: 'Nome',
    lastName: 'Cognome',
    birthTitle: 'Quando sei nato o nata?',
    birthDate: 'Data di nascita',
    birthHint: 'Nel formato di data del tuo dispositivo. Non si accettano date future.',
    genderTitle: 'Come descrivi il tuo genere?',
    genderIntro: 'Non viene usato per documenti ufficiali.',
    genders: {
      WOMAN: 'Donna',
      MAN: 'Uomo',
      NON_BINARY: 'Non binario',
      SELF_DESCRIBED: 'Lo descriverei diversamente',
      PREFER_NOT_TO_SAY: 'Preferisco non dirlo'
    },
    fiscalTitle: 'Il tuo codice fiscale',
    fiscalIntro: 'Puoi saltare questo passo e aggiungerlo più tardi nel profilo.',
    fiscalCode: 'Codice fiscale',
    fiscalPlaceholder: '16 caratteri, o 11 cifre se provvisorio',
    diaryTitle: 'Diario',
    todayLower: 'oggi',
    previousDay: 'Giorno precedente',
    nextDay: 'Giorno successivo',
    previousMonth: 'Mese precedente',
    nextMonth: 'Mese successivo',
    dailyRecord: 'Registrazione del giorno',
    record: 'Registra',
    editRecord: 'Modifica la registrazione',
    nothingRecorded: 'Ancora niente registrato',
    nothingRecordedIntro: 'Umore, ansia, irritabilità, sonno, peso e farmaci. Basta un minuto.',
    notYet: 'Questo giorno non è ancora arrivato.',
    completeness: { COMPLETE: 'Completa', PARTIAL: 'Parziale', NONE: 'Nessuna registrazione' },
    mood: 'Umore',
    anxiety: 'Ansia',
    irritability: 'Irritabilità',
    hoursSlept: 'Ore di sonno',
    weight: 'Peso',
    medication: 'Farmaci',
    skipped: 'Saltato',
    hours: 'ore',
    kg: 'kg',
    moods: [
      { name: 'Gravemente depresso', anchor: 'Compromissione significativa · non in grado di lavorare' },
      { name: 'Moderatamente depresso', anchor: 'Compromissione significativa · in grado di lavorare' },
      { name: 'Lievemente depresso', anchor: 'Senza compromissione significativa' },
      { name: 'Normale', anchor: '' },
      { name: 'Lievemente elevato', anchor: 'Senza compromissione significativa' },
      { name: 'Moderatamente elevato', anchor: 'Compromissione significativa · in grado di lavorare' },
      { name: 'Gravemente elevato', anchor: 'Compromissione significativa · non in grado di lavorare' }
    ],
    levels: [ 'Nessuna', 'Lieve', 'Moderata', 'Grave' ],
    monthChart: 'Grafico dell’umore',
    legend: 'Legenda',
    saveAsPdf: 'Salva come PDF',
    preparingPdf: 'Preparazione del PDF…',
    anxietyScale: 'Ansia 0–3',
    irritabilityScale: 'Irritabilità 0–3',
    emptyColumn: 'Colonna vuota · nessuna registrazione',
    emptyDays: 'Le colonne vuote sono giorni senza registrazione. Il peso si chiede il 28.',
    close: 'Chiudi',
    recordingFor: 'Registri per',
    recordStep: 'Registrazione · {step} di 6',
    recordSteps: 'Passaggi della registrazione',
    next: 'Avanti',
    skip: 'Salta',
    saveRecord: 'Salva',
    moodQuestion: 'Com’è stato il tuo umore oggi?',
    anxietyQuestion: 'Quanta ansia hai sentito oggi?',
    irritabilityQuestion: 'Quanto ti sei sentito o sentita irritabile oggi?',
    sleepQuestion: 'Quante ore hai dormito?',
    sleepHint: 'Il periodo concordato con il tuo clinico.',
    weightHint: 'Il tuo clinico lo chiede il 28.',
    medicationQuestion: 'Farmaci',
    medicationHint: 'Scrivi ciò che hai concordato con il tuo clinico di annotare ogni giorno.',
    addMedication: 'Aggiungi un farmaco',
    medicationName: 'Nome',
    dose: 'Dose (mg)',
    add: 'Aggiungi',
    removeMedication: 'Rimuovi {name}',
    removedNotRefreshed: 'Rimosso dalla tua lista. La lista non si è ancora aggiornata; lo sarà al prossimo passaggio.',
    noMedications: 'Nessun farmaco nella tua lista.',
    checkTitle: 'Controlla e salva',
    checkHint: 'Le risposte vuote restano vuote.',
    edit: 'Modifica',

    assignedToYou: 'Assegnati a te',
    nothingAssigned: 'Niente da compilare per ora.',
    completedTests: 'Completati',
    notStarted: 'Da iniziare',
    inProgress: 'In corso · bozza',
    submitted: 'Inviato',
    submittedOn: 'Inviato il {date}',
    fromClinician: 'Da {name}',
    dueOn: 'Entro il {date}',
    answeredOf: '{answered} risposte su {total}',
    repeatsEvery: 'Si ripete ogni {days} giorni',
    start: 'Inizia',
    saveAndExit: 'Salva ed esci',
    draftSaved: 'Bozza salvata',
    questionOf: 'Domanda {position} di {total}',
    answeredCount: '{answered} risposte',
    answered: 'Domande con risposta',
    previousQuestion: 'Domanda precedente',
    draftNote: 'Le tue risposte restano una bozza, visibile solo a te, finché non le invii.',
    checkAnswers: 'Controlla le risposte',
    backToQuestions: 'Torna alle domande',
    backToTests: 'Torna ai Test',
    missingOne: 'Una domanda non ha ancora una risposta. Rispondi a tutte le domande per inviare.',
    missingMany: '{count} domande non hanno ancora una risposta. Rispondi a tutte le domande per inviare.',
    notAnswered: 'Senza risposta',
    yourClinician: 'chi ti segue',
    submitNote: 'Quando invii, {name} può vedere le tue risposte. Fino ad allora sono una bozza che vedi solo tu.',
    submit: 'Invia',
    submitTo: 'Invia a {name}',
    yourAnswers: 'Le tue risposte',
    submittedKept: 'Inviato il {date}. Le risposte restano esattamente come le hai inviate.',

    goodMorning: 'Buongiorno, {name}',
    goodAfternoon: 'Buon pomeriggio, {name}',
    goodEvening: 'Buonasera, {name}',
    dailyRecords: 'Registrazioni giornaliere',
    thisWeek: 'Questa settimana',
    weekCounts: 'Complete {complete} · parziali {partial}',
    toCome: 'ancora da venire',
    nextAppointment: 'Prossimo appuntamento',
    minutes: '{minutes} min',
    formats: { IN_PERSON: 'In presenza', VIDEO: 'Video', PHONE: 'Telefono' },
    questionnairesBefore: 'Questionari prima di questa seduta',
    submittedOf: '{submitted} di {total} inviati',
    newNoteFrom: 'Nuova nota da {name}',
    fromSession: 'Dalla seduta del {date}',
    todaysRecord: 'La registrazione di oggi',
    notRecordedYet: 'Non ancora registrata · umore, ansia, irritabilità, sonno, farmaci',
    partlyRecorded: 'Registrata in parte · completala quando puoi',
    allRecorded: 'Tutto registrato',
    recordToday: 'Registra oggi',
    editTodaysRecord: 'Modifica la registrazione di oggi',
    questionnaires: 'Questionari',
    draftDetail: 'Bozza salvata · {answered} risposte su {total}',
    dueLower: 'entro il {date}',

    dashboard: 'Bacheca',
    patients: 'Pazienti',
    settings: 'Impostazioni',
    invitation: 'Invito',
    profileAndSettings: 'Profilo e impostazioni',
    profile: 'Profilo',
    gender: 'Genere',
    notAdded: 'Non inserito',
    appearance: 'Aspetto',
    appearances: {
      DEVICE: 'Dispositivo',
      LIGHT: 'Chiaro',
      DARK: 'Scuro'
    },
    appearanceDeviceHint: 'Segue il telefono o il computer: chiaro di giorno, scuro quando il dispositivo è in modalità scura.',
    appearanceChosenHint: 'Vale su ogni dispositivo con cui accedi.',

    invitationsWaiting: 'Inviti in attesa',
    noInvitationsWaiting: 'Nessun invito aspetta una risposta.',
    invitedNotAnswered: 'Invitato il {date} · ancora senza risposta',
    invitedExpired: 'Invitato il {date} · scaduto, invialo di nuovo per riaprirlo',
    declinedOn: 'Rifiutato il {date}',
    invitePatient: 'Invita un paziente',
    searchPatients: 'Cerca tra i pazienti',
    searchByName: 'Cerca per nome',
    filter: 'Filtro',
    active: 'Attivi',
    invited: 'Invitati',
    noPatientsFound: 'Ancora nessuno qui.',
    connectedSince: 'Collegato dal {date}',
    invitePatientTitle: 'Invita un paziente',
    patientEmail: 'Email del paziente',
    nameForList: 'Nome per la tua lista · facoltativo',
    nameForListHint: 'Lo vedi solo tu finché il paziente non completa il profilo.',
    whatHappensNext: 'Cosa succede dopo',
    inviteStep1: 'Riceve un’email con il link all’invito.',
    inviteStep2: 'Accede, o crea un account, e accetta o rifiuta.',
    inviteStep3: 'Quando accetta, compare nella tua lista dei pazienti e puoi leggere tutto il suo diario, comprese le registrazioni precedenti. Il suo journal resta privato.',
    sendInvitation: 'Invia l’invito',
    invitationSentTo: 'Invito inviato a {email}.',
    pending: 'In attesa',
    resendInvitation: 'Invia di nuovo',
    withdraw: 'Ritira',
    sentAgain: 'Inviato di nuovo.',

    yourClinicians: 'I tuoi clinici',
    noClinicians: 'Non sei ancora collegato con un clinico.',
    wantsToConnect: '{name} vuole collegarsi con te',
    invitationFrom: 'Invito del {date} · accetta o rifiuta',
    sentOn: 'Inviato il {date}',
    reviewInvitation: 'Vedi l’invito',
    wantsToConnectWithYou: '{name} vuole collegarsi con te',
    ifYouAccept: 'Se accetti, {name} può',
    canPlanSessions: 'Vedere e pianificare insieme le vostre sedute',
    canAssignQuestionnaires: 'Assegnarti questionari e leggere ciò che invii',
    canWriteNotes: 'Scriverti una nota dopo una seduta',
    canReadDiary: 'Leggere tutto il tuo diario, comprese le registrazioni che hai già scritto',
    journalStaysPrivate: 'Il tuo journal resta privato',
    acceptingSharesDiary: 'Se accetti, {name} può leggere tutto il tuo diario, comprese le registrazioni che hai già scritto. Il tuo journal non viene mai condiviso.',
    accept: 'Accetta',
    decline: 'Rifiuta',
    sentToOn: 'Inviato a {email} il {date}. Puoi rispondere anche più tardi da Impostazioni.',
    connectedWith: 'Sei collegato con {name}',
    diaryNowShared: 'Il tuo diario è ora condiviso con {name}, comprese le registrazioni che avevi già scritto. Il tuo journal resta privato.',
    invitationDeclined: 'Invito rifiutato',
    declinedExplained: 'Non sei collegato con {name}. Non è stato condiviso nulla di tuo. Se è stato un errore, chiedi a {name} di mandarti un nuovo invito.',
    goToToday: 'Vai a Oggi',
    invitationClosed: 'Questo invito non è più aperto',
    invitationClosedExplained: 'Ha già avuto una risposta, è stato ritirato, è scaduto o è stato mandato a un altro indirizzo email. I tuoi clinici sono elencati in Impostazioni.',
    openSettings: 'Apri Impostazioni',

    assignTest: 'Assegna un test',
    assignATest: 'Assegna un test',
    patientRecord: 'Scheda del paziente',
    openTests: 'Test aperti: {count}',
    searchTests: 'Cerca tra i test',
    assignedTests: 'Test assegnati',
    notSubmitted: 'Non inviato',
    noTestsFound: 'Ancora nessun test assegnato.',
    draftsStayWith: 'Le bozze restano a {name}: un test si apre qui quando è inviato.',
    assignedOn: 'Assegnato il {date}',
    byClinician: 'da {name}',
    questionnaire: 'Questionario',
    searchByNameOrTopic: 'Cerca per nome o argomento',
    availableIn: 'Disponibile in {languages}',
    noQuestionnairesFound: 'Nessun questionario corrisponde.',
    change: 'Cambia',
    languageNames: { en: 'inglese', it: 'italiano' },
    shownInLanguage: 'Mostrato nella lingua di {name}, {language}. Un questionario senza quella versione è mostrato in inglese.',
    shownInEnglish: 'La lingua di {name} è {language}. Questo questionario non ha una versione in {language}, quindi è mostrato in inglese.',
    shownOnlyIn: 'Questo questionario esiste solo in {language}, quindi è mostrato in {language}.',
    instructionsFor: 'Istruzioni per {name} · facoltative',
    assignStep1: '{name} lo trova in Test e in Oggi.',
    assignStep2: 'Le risposte di {name} restano private finché il test è una bozza.',
    assignStep3: 'Quando {name} lo invia, compare nella sua scheda Test.',
    assignTo: 'Assegna a {name}',
    waitingFor: 'In attesa di {name}',
    chooseQuestionnaire: 'Scegli un questionario da assegnare',

    toReview: 'Da rivedere',
    reviewed: 'Rivisto',
    reviewedLower: 'rivisto il {date}',
    newToReview: '{count} da rivedere',
    questionnairesToReview: 'Questionari da rivedere',
    nothingToReview: 'Niente aspetta la tua revisione.',
    submittedLower: 'inviato il {date}',
    allTests: 'Tutti i test',
    submittedAt: 'inviato {date}',
    notReviewed: 'Non rivisto',
    reviewedOn: 'Rivisto il {date}',
    totalScore: 'Totale',
    scores: 'Punteggi',
    answers: 'Risposte',
    answersHint: 'Apri una risposta per vedere tutte le opzioni e scrivere una nota. Le note si salvano mentre scrivi; le vedi solo tu e non arrivano mai a {name} né in un report per il paziente.',
    expandAll: 'Espandi tutto',
    collapseAll: 'Comprimi tutto',
    note: 'Nota',
    theirAnswer: '(risposta di {name})',
    yourNote: 'La tua nota',
    noteOnAnswer: 'Aggiungi una nota su questa risposta',
    saving: 'Salvataggio…',
    saved: 'Salvata',
    yourReview: 'La tua revisione',
    noteOnTest: 'La tua nota su questo test',
    noteOnTestPlaceholder: 'Aggiungi una nota sull’intero test',
    reviewHint: 'La vedi solo tu. Scrivere note non segna il test come rivisto, e {name} non vede mai lo stato della revisione.',
    markReviewed: 'Segna come rivisto',
    markNotReviewed: 'Segna come non rivisto',
    earlierAttempts: 'Tentativi precedenti',
    keptAsSubmitted: 'Ogni tentativo resta esattamente come è stato inviato.',

    loadFailed: 'Impossibile caricare la pagina.',
    loadFailedIntro: 'Quello che hai salvato è al sicuro. Controlla la connessione e riprova.',
    progressFailed: 'Impossibile caricare i progressi aggiornati.',
    tryAgain: 'Riprova',

    landing: {
      signIn: 'Accedi',
      kicker: 'Per chi è in terapia, e per i clinici che se ne prendono cura',
      title: 'Tieni traccia tra una seduta e l’altra, insieme al tuo clinico.',
      intro: 'psy accompagna la cura che hai già: la registrazione del giorno che il tuo clinico ti chiede, i questionari che ti assegna, le tue sedute e le note che scrive per te. Dal telefono o dal computer, in italiano o in inglese.',
      start: 'Accedi con l’email',
      invited: 'Ti ha invitato il tuo clinico? Usa l’indirizzo email a cui è arrivato l’invito.',
      thisWeek: 'Questa settimana',
      week: [ 'L', 'M', 'M', 'G', 'V', 'S', 'D' ],
      nextAppointment: 'Prossimo appuntamento',
      appointmentWhen: 'Gio 15 ott · 17:00',
      appointmentHow: '50 min · In presenza',
      newNote: 'Una nuova nota dal tuo clinico',
      forYou: 'Per te',
      patient: [
        { title: 'Registrazione del giorno', text: 'Umore, ansia, irritabilità, sonno, peso e farmaci, una domanda alla volta. Salta ciò che non sai: nulla viene compilato al posto tuo.' },
        { title: 'Questionari', text: 'Compila ciò che il tuo clinico ti assegna, con i tuoi tempi. Una bozza resta tua finché non la invii.' },
        { title: 'Sedute e note', text: 'Vedi il tuo prossimo appuntamento e leggi la nota che il tuo clinico scrive per te dopo una seduta.' },
        { title: 'Journal', text: 'Scrivi ciò che vuoi ricordare o portare alla prossima seduta. Resta privato.' }
      ],
      forClinicians: 'Per i clinici',
      clinician: [
        { title: 'I tuoi pazienti', text: 'Invita un paziente via email e vedi in un unico elenco tutte le persone che segui.' },
        { title: 'Questionari', text: 'Assegna un questionario nella lingua del paziente, poi rivedi ogni risposta e i punteggi pubblicati, con note private.' },
        { title: 'Grafico dell’umore', text: 'Leggi il mese di un paziente come sulla tua scheda cartacea: i sette stati dell’umore, ansia, irritabilità, sonno e peso.' },
        { title: 'Report PDF', text: 'Salva in PDF un test inviato o un mese di diario, per la tua documentazione.' }
      ],
      sharedTitle: 'Cosa viene condiviso, e con chi',
      shared: [
        'Ti colleghi solo con un clinico che ti invita, e solo se accetti.',
        'Accettando, quel clinico può leggere il tuo diario, comprese le registrazioni che avevi già scritto. Trovi i clinici a cui sei collegato nelle Impostazioni.',
        'Il tuo journal non viene mai condiviso.',
        'Le note private di un clinico restano sue. Tu vedi la nota che scrive per te.',
        'psy non fa diagnosi e non dà consigli medici.'
      ],
      howItStarts: 'Come si comincia',
      steps: [
        { title: 'Il tuo clinico ti invita', text: 'Ricevi un’email con il link all’invito.' },
        { title: 'Accedi e accetti', text: 'Accedi con un codice inviato a quell’indirizzo email. Prima di accettare, leggi esattamente cosa verrà condiviso.' },
        { title: 'Tenete traccia insieme', text: 'Registra le tue giornate e compila i questionari; il tuo clinico li legge prima delle sedute.' }
      ],
      languages: 'In italiano o in inglese, secondo la lingua del tuo dispositivo.'
    },

    errors: {
      'psy could not be reached. Check your connection and try again': 'Impossibile raggiungere psy. Controlla la connessione e riprova',
      'Something went wrong. Try again': 'Qualcosa è andato storto. Riprova',
      'You are signed out. This page could not be updated; try again': 'Sei uscito. Questa pagina non si è potuta aggiornare; riprova',
      'This medication was not found': 'Questo farmaco non è stato trovato',
      'Write one entry for each medication': 'Scrivi una sola annotazione per ogni farmaco',
      'Enter your email address': 'Inserisci il tuo indirizzo email',
      'Enter a valid email address': 'Inserisci un indirizzo email valido',
      'Enter the 6-digit code from the email': 'Inserisci il codice di 6 cifre ricevuto per email',
      'Wait a few seconds before asking for another code': 'Aspetta qualche secondo prima di chiedere un altro codice',
      'That code is not right. Check it and try again': 'Il codice non è corretto. Controllalo e riprova',
      'This code is no longer valid. Ask for a new one': 'Questo codice non è più valido. Chiedine uno nuovo',
      'This code has expired. Ask for a new one': 'Questo codice è scaduto. Chiedine uno nuovo',
      'Too many attempts. Ask for a new code': 'Troppi tentativi. Chiedi un nuovo codice',
      'Enter your first name': 'Inserisci il tuo nome',
      'Enter your last name': 'Inserisci il tuo cognome',
      'A name can be at most 100 characters': 'Un nome può avere al massimo 100 caratteri',
      'Enter your birth date': 'Inserisci la tua data di nascita',
      'Choose one option': 'Scegli un’opzione',
      'Enter a valid birth date': 'Inserisci una data di nascita valida',
      'The birth date cannot be in the future': 'La data di nascita non può essere nel futuro',
      'Enter a birth date after 1900': 'Inserisci una data di nascita successiva al 1900',
      'A fiscal code has 16 letters and digits, or 11 digits if temporary': 'Il codice fiscale ha 16 lettere e cifre, o 11 cifre se provvisorio',
      'Sign in to continue': 'Accedi per continuare',
      'Choose a valid day': 'Scegli un giorno valido',
      'You cannot record a day that has not happened yet': 'Non puoi registrare un giorno che non è ancora arrivato',
      'Enter the hours slept, from 0 to 24': 'Inserisci le ore dormite, da 0 a 24',
      'Enter the weight in kg, for example 70.5': 'Inserisci il peso in kg, per esempio 70,5',
      'Enter the medication’s name': 'Inserisci il nome del farmaco',
      'A dose can be at most 20 characters': 'Una dose può avere al massimo 20 caratteri',
      'An entry can be at most 200 characters': 'Una nota può avere al massimo 200 caratteri',
      'Choose a level from 0 to 3': 'Scegli un livello da 0 a 3',
      'Choose one of the seven moods': 'Scegli uno dei sette stati d’umore',
      'Only patients take questionnaires': 'Solo i pazienti compilano i questionari',
      'This questionnaire was not found': 'Questo questionario non è stato trovato',
      'This questionnaire has already been submitted': 'Questo questionario è già stato inviato',
      'Choose one of the answers': 'Scegli una delle risposte',
      'Answer every question before submitting': 'Rispondi a tutte le domande prima di inviare',
      'Only clinicians have patients': 'Solo i clinici hanno pazienti',
      'Only patients answer invitations': 'Solo i pazienti rispondono agli inviti',
      'You cannot invite yourself': 'Non puoi invitare te stesso',
      'This person is already one of your patients': 'Questa persona è già tra i tuoi pazienti',
      'You have already invited this address. Send it again from the list': 'Hai già invitato questo indirizzo. Invialo di nuovo dalla lista',
      'Wait a few seconds before sending it again': 'Aspetta qualche secondo prima di inviarlo di nuovo',
      'This invitation was not found': 'Questo invito non è stato trovato',
      'This patient was not found': 'Questo paziente non è stato trovato',
      'This test can no longer be withdrawn': 'Questo test non si può più ritirare',
      'Instructions can be at most 500 characters': 'Le istruzioni possono avere al massimo 500 caratteri',
      'This test was not found': 'Questo test non è stato trovato',
      'Choose a valid month': 'Scegli un mese valido',
      'Not found': 'Non trovato',
      'This answer was not found': 'Questa risposta non è stata trovata',
      'A note can be at most 2000 characters': 'Una nota può avere al massimo 2000 caratteri',
      'This choice was made while someone else was signed in. Reload the page': 'Questa scelta è stata fatta mentre era connesso qualcun altro. Ricarica la pagina',
      'This note was written while someone else was signed in. Reload the page': 'Questa nota è stata scritta mentre era connesso qualcun altro. Ricarica la pagina',
      'A note is not saved yet. Sign out again to try saving it, or sign out without it': 'Una nota non è ancora salvata. Esci di nuovo per riprovare a salvarla, oppure esci senza salvarla'
    }
  }
};
